import React, { useEffect, useMemo, useState } from 'react';

import { Center, Stack, Text } from '@mantine/core';
import useKiloClassesByTypeAndDate from 'hooks/class/useKiloClassesByTypeAndDate';
import type { WorkoutRetrieve } from 'types';

import AthletesTable from './components/AthletesTable';
import ClassTimeSelector from './components/ClassTimeSelector';

interface WhiteboardResultsTabProps {
  selectedTypeId: string | null;
  date: string;
  workout: WorkoutRetrieve | null;
  initialClassId?: string;
}

const WhiteboardResultsTab = ({
  selectedTypeId,
  date,
  workout,
  initialClassId,
}: WhiteboardResultsTabProps): JSX.Element => {
  const [resultsClassId, setResultsClassId] = useState<string | null>(initialClassId ?? null);

  const {
    data: classInstancesData,
    isLoading: isInstancesLoading,
    isFetched: isInstancesFetched,
  } = useKiloClassesByTypeAndDate({
    kiloClassTypeId: workout ? selectedTypeId ?? undefined : undefined,
    date,
  });

  // The API doesn't guarantee chronological order — sort ascending by startTime so
  // both the Chip list and the default-selected instance below are earliest-first.
  const classInstances = useMemo(
    () => [...(classInstancesData?.data ?? [])].sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [classInstancesData],
  );

  useEffect(() => {
    if (isInstancesLoading || !isInstancesFetched) return;
    if (classInstances.length === 0) {
      setResultsClassId(null);
      return;
    }
    setResultsClassId(prev => {
      if (prev && classInstances.some(i => i.kiloClassId === prev)) return prev;
      return classInstances[0].kiloClassId;
    });
  }, [classInstances, isInstancesLoading, isInstancesFetched]);

  if (!workout) {
    return (
      <Center py='xl'>
        <Text color='dimmed'>No workout assigned to this class.</Text>
      </Center>
    );
  }

  return (
    <Stack spacing='md'>
      {classInstances.length > 1 && (
        <ClassTimeSelector
          classInstances={classInstances}
          selectedId={resultsClassId}
          onChange={setResultsClassId}
        />
      )}

      {resultsClassId ? (
        <AthletesTable classId={resultsClassId} workout={workout} />
      ) : (
        <Center py='xl'>
          <Text color='dimmed'>Select a class time to view athletes.</Text>
        </Center>
      )}
    </Stack>
  );
};

export default WhiteboardResultsTab;
